// ===== 工程実施タブ =====

import { useState, useMemo, useRef, useCallback, Fragment } from "react";
import {
  Stack, Typography, Paper, Box, Button, Table, TableHead, TableBody, TableRow, TableCell, IconButton,
  Collapse, Checkbox, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { PageContext } from "../PageContext";
import { ExtendedDB } from "../../../types/extendedDB";
import { HierarchicalView, HierarchyBuilder, HierarchyNode, LeafRowInfo } from "../../../components/HierarchicalView";
import { getAreaSlots, slotsToText } from "../../../util/areaStatus";
import { areaLabel, workDetailName, buildProcessExecutionCandidates, materialNamesForWorkDetail } from "./helpers";
import { errorMessageOf } from "../../../util/typed";

// 追加ダイアログの階層ビュー末端(施工箇所)ノードに載せるデータ。
type AddLeafData = { areaId: string };

export function ProcessExecutionTab({
  pageContext, db, constructionId, ensureConstruction, planId, reload, setSnackbar,
}: {
  pageContext: PageContext; db: ExtendedDB; constructionId: string | null;
  ensureConstruction: (planOverride?: string) => Promise<string>; planId: string;
  reload: () => Promise<unknown> | void;
  setSnackbar: (s: string) => void;
}) {
  const rows = useMemo(
    () => db.processExecutions.filter((p) => p.construction_id === constructionId && p.deleted_at == null),
    [db.processExecutions, constructionId]
  );

  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const [addOpen, setAddOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [expandedAddAreaIds, setExpandedAddAreaIds] = useState<Set<string>>(new Set());

  const openAdd = () => { setPicked(new Set()); setAddOpen(true); };

  const pairKey = (areaId: string, wdId: number) => `${areaId}|${wdId}`;
  const togglePick = useCallback((key: string) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(key)) { n.delete(key); } else { n.add(key); }
      return n;
    });
  }, []);
  const toggleAddAreaExpand = useCallback((areaId: string) => {
    setExpandedAddAreaIds((prev) => {
      const n = new Set(prev);
      if (n.has(areaId)) { n.delete(areaId); } else { n.add(areaId); }
      return n;
    });
  }, []);

  // 追加候補(施工箇所×工程)。既存 (areaId, workDetailId) は候補から出ない(buildProcessExecutionCandidates, A-4-3)。
  const candidates = useMemo(
    () => buildProcessExecutionCandidates(db, planId, rows),
    [db, planId, rows]
  );
  const candidateAreaIds = useMemo(() => Array.from(new Set(candidates.map((c) => c.areaId))), [candidates]);

  // 場所区分階層ビルダ(施工実施一覧と同じ locationAxisBuilder 相当)。末端は施工箇所(1 area = 1 leaf)。
  const addBuilder: HierarchyBuilder<AddLeafData> = useCallback((path: string[]) => {
    const slotDivOf = (areaId: string, slot: number): number | null => {
      const hit = getAreaSlots(areaId, db.constructionPlanAreaLocations).find((s) => s.slot === slot);
      return hit?.location_division_id ?? null;
    };
    const candidateAreas = candidateAreaIds
      .map((id) => db.constructionPlanAreas.find((a) => a.id === id))
      .filter((a): a is NonNullable<typeof a> => a != null);
    const filtered = candidateAreas.filter((a) => {
      for (let i = 0; i < path.length; i++) {
        const slot = i + 1;
        const expected = path[i] === "none" ? null : Number(path[i]);
        if (slotDivOf(a.id, slot) !== expected) {
          return false;
        }
      }
      return true;
    });
    const nextSlot = path.length + 1;
    const groups = new Map<string, typeof candidateAreas>();
    for (const a of filtered) {
      const v = slotDivOf(a.id, nextSlot);
      const k = v == null ? "none" : String(v);
      const arr = groups.get(k) ?? [];
      arr.push(a);
      groups.set(k, arr);
    }
    const allLeavesNow =
      nextSlot > 10
      || (groups.size === 1 && groups.has("none"))
      || filtered.every((ar) => {
        for (let s = nextSlot; s <= 10; s++) {
          if (slotDivOf(ar.id, s) != null) {
            return false;
          }
        }
        return true;
      });
    if (allLeavesNow) {
      return {
        title: "施工箇所",
        rows: filtered.map((ar): HierarchyNode<AddLeafData> => ({
          key: ar.id,
          label: slotsToText(ar.id, db.constructionPlanAreaLocations, db.locationDivisions) || "(場所未設定)",
          navigable: false,
          data: { areaId: ar.id },
        })),
      };
    }
    return {
      title: "場所区分",
      rows: Array.from(groups.entries()).sort((x, y) => x[0].localeCompare(y[0])).map(([k2, list]) => {
        let label = "(未設定)";
        if (k2 !== "none") {
          label = db.locationDivisions.find((d) => d.id === Number(k2))?.division_name ?? k2;
        }
        return { key: k2, label: label, count: list.length };
      }),
    };
  }, [candidateAreaIds, db]);

  // 当該施工箇所で選択可能な候補(areaId, workDetailId)ペア。
  const candidatesForArea = (areaId: string) => candidates.filter((c) => c.areaId === areaId);
  const toggleAreaAll = (areaId: string) => {
    const keys = candidatesForArea(areaId).map((c) => pairKey(c.areaId, c.workDetailId));
    if (keys.length === 0) {
      return;
    }
    const allSelected = keys.every((k) => picked.has(k));
    setPicked((prev) => {
      const n = new Set(prev);
      if (allSelected) {
        for (const k of keys) { n.delete(k); }
      } else {
        for (const k2 of keys) { n.add(k2); }
      }
      return n;
    });
  };

  // 追加ダイアログの施工箇所 leaf を「施工箇所アコーディオン + 配下工程チェック + 全工程一括列」で描画(mock 準拠)。
  const renderAddLeaves = (leafRows: LeafRowInfo<AddLeafData>[]) => (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 36, p: 0.5 }} />
            <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 130 }}>工種 / 種別</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 90 }} align="center">追加可能工程数</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 64 }} align="center">全工程</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leafRows.map((r) => {
            const areaId = r.node.data?.areaId;
            const area = areaId != null ? db.constructionPlanAreas.find((a) => a.id === areaId) : null;
            if (area == null) {
              return null;
            }
            const wt = db.workTypes.find((w) => w.id === area.work_type_id);
            const wst = db.workSubtypes.find((w) => w.id === area.work_subtype_id);
            const areaCandidates = candidatesForArea(area.id);
            const selKeys = areaCandidates.map((c) => pairKey(c.areaId, c.workDetailId));
            const selCount = selKeys.filter((k) => picked.has(k)).length;
            const expanded = expandedAddAreaIds.has(area.id);
            return (
              <Fragment key={area.id}>
                <TableRow hover
                  sx={{ cursor: "pointer", borderLeft: 4, borderLeftColor: "success.main",
                    bgcolor: expanded ? "rgba(46,125,50,0.08)" : "rgba(46,125,50,0.04)",
                    "& > td": { borderBottom: expanded ? "none" : undefined } }}
                  onClick={() => toggleAddAreaExpand(area.id)}>
                  <TableCell sx={{ p: 0.5 }}>
                    <IconButton size="small">{expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}</IconButton>
                  </TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{areaLabel(db, area.id)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{wt?.name ?? "-"} / {wst?.name ?? "-"}</Typography></TableCell>
                  <TableCell align="center"><Chip size="small" variant="outlined" label={areaCandidates.length} sx={{ pointerEvents: "none" }} /></TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    {selKeys.length === 0
                      ? <Typography variant="caption" color="text.secondary">-</Typography>
                      : <Checkbox size="small" checked={selCount === selKeys.length} indeterminate={selCount > 0 && selCount < selKeys.length} onChange={() => toggleAreaAll(area.id)} />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 2, py: 1, bgcolor: "grey.50" }}>
                        <Table size="small">
                          <TableBody>
                            {areaCandidates.length === 0 && (
                              <TableRow><TableCell sx={{ color: "text.secondary" }}>追加できる工程がありません(すべて登録済み)</TableCell></TableRow>
                            )}
                            {areaCandidates.map((c) => {
                              const k = pairKey(c.areaId, c.workDetailId);
                              return (
                                <TableRow key={c.workDetailId}>
                                  <TableCell sx={{ width: 36, p: 0.5 }} />
                                  <TableCell>{workDetailName(db, c.workDetailId)}</TableCell>
                                  <TableCell align="center" sx={{ width: 64 }}>
                                    <Checkbox size="small" checked={picked.has(k)} onChange={() => togglePick(k)} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );

  const commitAdd = async () => {
    if (lock.current) { return; }
    if (picked.size === 0) { setAddOpen(false); return; }
    // 複数 plan にまたがる選択は保存を拒否する(plan 未確定時の導出が不整合になるため)。
    const pickedAreaIds = new Set(Array.from(picked).map((k) => k.split("|")[0]));
    const pickedPlanIds = new Set(
      Array.from(pickedAreaIds)
        .map((id) => db.constructionPlanAreas.find((a) => a.id === id)?.construction_plan_id)
        .filter((x): x is string => x != null)
    );
    if (planId === "" && pickedPlanIds.size > 1) {
      pageContext.api.showError("複数の施工計画にまたがる施工箇所は同時に追加できません。1つの施工計画の範囲で選び直してください。");
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      // plan 未確定時は選択した施工箇所から導出する。
      const firstKey = Array.from(picked)[0];
      const firstArea = db.constructionPlanAreas.find((a) => a.id === firstKey?.split("|")[0]);
      const cid = await ensureConstruction(firstArea?.construction_plan_id);
      for (const key of Array.from(picked)) {
        const parts = key.split("|");
        await pageContext.api.entities.putRow("processExecutions", {
          construction_id: cid,
          construction_plan_area_id: parts[0],
          work_detail_id: Number(parts[1]),
          completed_at: null,
          remarks: null,
        });
      }
      setSnackbar(`${picked.size} 件の工程実施を追加しました`);
      setAddOpen(false);
      setPicked(new Set());
      await reload();
    } catch (e) {
      pageContext.api.showError(`追加に失敗しました: ${errorMessageOf(e)}`);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  const toggleCompleted = async (id: string, next: boolean) => {
    if (lock.current) return;
    const row = rows.find((r) => r.id === id);
    if (!row) {
      pageContext.api.showError("対象の工程実施が見つかりません。");
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      // 生成 patch schema は serverGenerate 以外の列を要求するため、未編集列は既存値を同送する。
      await pageContext.api.entities.patchRow("processExecutions", {
        id,
        construction_id: row.construction_id,
        construction_plan_area_id: row.construction_plan_area_id,
        work_detail_id: row.work_detail_id,
        completed_at: next ? Date.now() : null,
        remarks: row.remarks,
      });
      await reload();
    } catch (e) {
      pageContext.api.showError(`更新に失敗しました: ${errorMessageOf(e)}`);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  const del = async (id: string) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      // 工程実施を削除すると配下の消費行も同時に落とす(材料が消費済みのまま座礁しないように)。
      await pageContext.api.entities.setDeleteWithConsumptionCascade("processExecutions", id, true);
      setSnackbar("工程実施を削除しました");
      await reload();
    } catch (e) {
      pageContext.api.showError(`削除に失敗しました: ${errorMessageOf(e)}`);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <Stack gap={2}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={busy} sx={{ whiteSpace: "nowrap" }}>工程実施を追加</Button>
      </Box>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>まだ登録がありません。</Typography>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>完了</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>材料</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const area = db.constructionPlanAreas.find((a) => a.id === r.construction_plan_area_id);
                const subtype = area?.work_subtype_id != null ? db.workSubtypes.find((s) => s.id === area.work_subtype_id) : null;
                const mats = materialNamesForWorkDetail(db, r.work_detail_id);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" fontWeight={600}>{areaLabel(db, r.construction_plan_area_id)}</Typography>
                        {subtype != null && <Chip size="small" variant="outlined" label={subtype.name} sx={{ pointerEvents: "none" }} />}
                      </Stack>
                    </TableCell>
                    <TableCell>{workDetailName(db, r.work_detail_id)}</TableCell>
                    <TableCell>
                      <Button size="small" disabled={busy} onClick={() => toggleCompleted(r.id, r.completed_at == null)}>
                        {r.completed_at != null ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="完了" sx={{ pointerEvents: "none" }} /> : <Chip size="small" color="warning" variant="outlined" label="未完了" sx={{ pointerEvents: "none" }} />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {mats.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      ) : (
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {mats.map((m) => <Chip key={m} size="small" variant="outlined" label={m} sx={{ pointerEvents: "none" }} />)}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" disabled={busy} onClick={() => del(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>工程実施を追加(施工箇所 × 工程)</DialogTitle>
        <DialogContent dividers>
          {candidates.length === 0 ? (
            <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>追加できる施工箇所がありません(すべて登録済み、または施工箇所がありません)。</Typography>
          ) : (
            <Stack gap={1}>
              <Typography variant="body2" color="text.secondary">
                施工箇所を開いて、今回施工する工程にチェックを入れてください。施工箇所・工程は複数選べます。右端の「全工程」で、その施工箇所の未追加工程をまとめて選択できます。
              </Typography>
              <HierarchicalView
                builder={addBuilder}
                renderLeaves={renderAddLeaves}
                flattenThreshold={8}
                emptyText="追加できる施工箇所がありません"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Box flex={1} sx={{ pl: 1 }}>
            <Typography variant="caption" color="text.secondary">{picked.size} 件の工程実施を追加します</Typography>
          </Box>
          <Button onClick={() => setAddOpen(false)}>キャンセル</Button>
          <Button variant="contained" disabled={busy || picked.size === 0} onClick={commitAdd}>
            {picked.size > 0 ? `${picked.size} 件を追加` : "追加"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
